import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Checkbox,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { Calculate, Add, Delete, Visibility, WarningAmber, CallMerge } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import invoiceService from "../../services/invoiceService";
import userService from "../../services/userService";
import promoService from "../../services/promoService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import InvoicePreviewDialog from "../../components/common/InvoicePreviewDialog";
import StatusChip from "../../components/common/StatusChip";
import { selectUsdToCiRate } from "../../redux/slices/systemSettingsSlice";
import { selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";
import { ciToUsd } from "../../utils/currency";

const blankCalcInputs = {
  cbf: "",
  weightLb: "",
  merchandiseValue: "",
  deliverySelected: false,
};

const AdminGenerateInvoice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Arrived via AdminConsolidations' "Invoice Consolidation" button —
  // auto-selects every un-invoiced item in this consolidation once the
  // (mode 0 / Browse All) shipment list has loaded. See the effect below
  // and selectFullConsolidation.
  const consolidationIdParam = searchParams.get("consolidationId");
  const autoSelectedConsolidationRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const usdToCiRate = useSelector(selectUsdToCiRate);
  const consolidationCompletedKey = useSelector(selectKeyForRole("consolidation", "completed"));

  const [mode, setMode] = useState(0); // 0 = Browse All, 1 = Search Customer
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(true);

  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomerOptions, setLoadingCustomerOptions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchDebounceRef = useRef(null);

  // itemId -> { shipmentId, itemId, customerId }
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [calcInputs, setCalcInputs] = useState(blankCalcInputs);
  const [calcLines, setCalcLines] = useState([]);
  // Ad-hoc charges the admin adds on top of the calculated lines above —
  // anything not covered by the standard freight/duty/service calculation
  // (e.g. a storage fee, a re-crate charge, a one-off adjustment).
  const [extraLines, setExtraLines] = useState([]);
  const extraLineIdRef = useRef(0);
  // Duties — billed-to-customer amount, added as its own invoice line.
  // Separate from Invoice.dutyAmount (the cost-tracking "what Waypoint
  // actually paid Customs" figure, set elsewhere) — this is what's charged.
  const [dutiesAmount, setDutiesAmount] = useState("");
  // Discount — admin picks either a flat CI$ amount off the top, or a
  // percentage computed off everything else already entered (calculated
  // lines + extra charges + duties, i.e. the fresh charges on THIS
  // invoice — never applied to carried-forward previous-dues lines or
  // locked-in promo redemptions, since those are fixed historical amounts,
  // not something a one-off discount on this invoice should touch).
  const [discountType, setDiscountType] = useState("amount"); // "amount" | "percent"
  const [discountValue, setDiscountValue] = useState("");
  const [freightBySailing, setFreightBySailing] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);

  // Customer's other still-unpaid invoices — carried forward onto this one
  // as "Previous pending from Invoice X" lines so an old balance doesn't
  // silently sit forgotten on a separate invoice. Admin can uncheck any of
  // them to leave that one out of this invoice.
  const [previousDues, setPreviousDues] = useState([]);
  const [includedDueIds, setIncludedDueIds] = useState(new Set());
  const [loadingDues, setLoadingDues] = useState(false);

  // Promo discounts already redeemed by the customer (at shipment-creation
  // time — see ShipmentForm.jsx's "Promo Code" section) against
  // whichever of the currently-selected shipments haven't been billed on
  // an invoice yet. Nothing to toggle here — the decision was already made
  // and locked in when the shipment was created; this is purely informational
  // and auto-applies. Refetches whenever the item selection changes, since
  // it depends on which shipments are actually being billed right now.
  const [promoDiscounts, setPromoDiscounts] = useState([]);
  const [loadingPromoDiscounts, setLoadingPromoDiscounts] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);

  const loadShipments = async (params) => {
    setLoadingShipments(true);
    try {
      const { data } = await invoiceService.getInvoiceableShipments(params);
      setShipments(data.data.shipments || []);
    } catch {
      enqueueSnackbar("Failed to load invoiceable shipments", { variant: "error" });
    } finally {
      setLoadingShipments(false);
    }
  };

  useEffect(() => {
    if (mode === 0) loadShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode !== 1) return;
    let active = true;
    const search = customerQuery.trim();
    if (!search || search.length < 2) {
      setCustomerOptions([]);
      return () => {
        active = false;
        clearTimeout(searchDebounceRef.current);
      };
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setLoadingCustomerOptions(true);
      try {
        const { data } = await userService.getAll({ role: "customer", search, limit: 10 });
        if (active) setCustomerOptions(data.data || []);
      } catch {
        if (active) setCustomerOptions([]);
      } finally {
        if (active) setLoadingCustomerOptions(false);
      }
    }, 600);
    return () => {
      active = false;
      clearTimeout(searchDebounceRef.current);
    };
  }, [customerQuery, mode]);

  const handleSelectCustomer = (_, customer) => {
    setSelectedCustomer(customer);
    if (customer) loadShipments({ customer: customer._id });
    else setShipments([]);
  };

  // Once anything is selected, lock the picker to that item's customer —
  // an invoice can only bill one customer.
  const activeCustomerId = useMemo(() => {
    const first = [...selectedItems.values()][0];
    return first?.customerId || null;
  }, [selectedItems]);

  const itemRefs = useMemo(
    () => [...selectedItems.values()].map((v) => ({ shipmentId: v.shipmentId, itemId: v.itemId })),
    [selectedItems],
  );

  const distinctShipmentIds = useMemo(
    () => [...new Set([...selectedItems.values()].map((v) => v.shipmentId))],
    [selectedItems],
  );

  // Selected items still sitting in a consolidation that hasn't shipped/
  // delivered as one unit yet — surfaced as a warning, not a block, since
  // an admin may deliberately want to bill one item out early rather than
  // wait for the rest of the consolidation.
  const selectedIncompleteConsolidationItems = useMemo(() => {
    const result = [];
    for (const shipment of shipments) {
      for (const item of shipment.items || []) {
        if (!selectedItems.has(item._id)) continue;
        if (item.consolidation && item.consolidation.status !== consolidationCompletedKey) {
          result.push({ item, shipment });
        }
      }
    }
    return result;
  }, [shipments, selectedItems, consolidationCompletedKey]);

  // Grouped Sailing → Shipment → Item, same structure/sort as Item Tracking
  // and the Sailing Detail page — an item's sailingSchedule is populated by
  // getInvoiceableShipments for exactly this. Items with no sailing
  // assigned yet land in their own trailing group.
  const sailingGroups = useMemo(() => {
    const visibleShipments = shipments.filter((s) => !activeCustomerId || s.customer?._id === activeCustomerId);
    const bySailing = new Map();

    for (const shipment of visibleShipments) {
      const uninvoicedItems = (shipment.items || []).filter((i) => !i.invoiced);
      for (const item of uninvoicedItems) {
        const sailing = item.sailingSchedule;
        const key = sailing?._id || "no-sailing";
        if (!bySailing.has(key)) {
          bySailing.set(key, {
            sailingId: key,
            sailingNumber: sailing?.sailingNumber,
            vesselName: sailing?.vesselName,
            voyageNumber: sailing?.voyageNumber,
            masterBillNumber: sailing?.masterBillNumber,
            status: sailing?.status,
            departureDate: sailing?.departureDate,
            estimatedArrivalDate: sailing?.estimatedArrivalDate,
            shipmentsMap: new Map(),
          });
        }
        const group = bySailing.get(key);
        if (!group.shipmentsMap.has(shipment._id)) {
          group.shipmentsMap.set(shipment._id, { shipment, items: [] });
        }
        group.shipmentsMap.get(shipment._id).items.push(item);
      }
    }

    return [...bySailing.values()]
      .map((g) => ({ ...g, shipments: [...g.shipmentsMap.values()] }))
      .sort((a, b) => {
        if (a.sailingId === "no-sailing") return 1;
        if (b.sailingId === "no-sailing") return -1;
        return new Date(b.departureDate) - new Date(a.departureDate);
      });
  }, [shipments, activeCustomerId]);

  useEffect(() => {
    if (!activeCustomerId) {
      setPreviousDues([]);
      setIncludedDueIds(new Set());
      return;
    }
    setLoadingDues(true);
    invoiceService
      .getOutstanding({ customer: activeCustomerId })
      .then(({ data }) => {
        const dues = data.data.invoices || [];
        setPreviousDues(dues);
        setIncludedDueIds(new Set(dues.map((d) => d._id)));
      })
      .catch(() => setPreviousDues([]))
      .finally(() => setLoadingDues(false));
  }, [activeCustomerId]);

  const toggleDueIncluded = (id) => {
    setIncludedDueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const previousDueLines = previousDues
    .filter((d) => includedDueIds.has(d._id))
    .map((d) => ({
      description: `Previous pending from Invoice ${d.invoiceNumber}`,
      unitPrice: d.balanceDue,
      quantity: 1,
    }));

  const shipmentIdsKey = distinctShipmentIds.join(",");
  useEffect(() => {
    if (distinctShipmentIds.length === 0) {
      setPromoDiscounts([]);
      return;
    }
    setLoadingPromoDiscounts(true);
    promoService
      .getForShipments(distinctShipmentIds)
      .then(({ data }) => setPromoDiscounts(data.data.discounts || []))
      .catch(() => setPromoDiscounts([]))
      .finally(() => setLoadingPromoDiscounts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentIdsKey]);

  const promoLines = promoDiscounts.map((d) => ({
    description: `Promo: ${d.promoName} (${d.shipmentTrackingNumber})`,
    unitPrice: -Math.abs(d.discountAmount),
    quantity: 1,
  }));

  const runCalculate = async (overrides = {}) => {
    if (!itemRefs.length) {
      setCalcLines([]);
      setFreightBySailing([]);
      return;
    }
    setCalculating(true);
    setCalcError("");
    try {
      const { data } = await invoiceService.calculateItemsCharges({
        items: itemRefs,
        customerId: activeCustomerId,
        deliverySelected: calcInputs.deliverySelected,
        ...overrides,
      });
      const charges = data.data.charges;
      setCalcLines(charges.lines);
      setFreightBySailing(data.data.freightBySailing || []);
      if (overrides.resetTotals) {
        setCalcInputs((p) => ({
          ...p,
          cbf: charges.inputs.cbf,
          weightLb: charges.inputs.weightLb,
          merchandiseValue: charges.inputs.merchandiseValue,
        }));
      }
    } catch {
      setCalcError("Failed to calculate charges for the selected items");
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    runCalculate({ resetTotals: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const toggleItem = (shipment, item) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(item._id)) {
        next.delete(item._id);
      } else {
        next.set(item._id, {
          shipmentId: shipment._id,
          itemId: item._id,
          customerId: shipment.customer?._id,
        });
      }
      return next;
    });
  };

  // Select/deselect every un-invoiced item on a shipment at once — the
  // admin no longer has to click through 100s of items one by one when the
  // whole shipment is going on the same invoice.
  const shipmentAllSelected = (items) => items.length > 0 && items.every((i) => selectedItems.has(i._id));
  const shipmentSomeSelected = (items) => items.some((i) => selectedItems.has(i._id));

  // Selects every un-invoiced item belonging to one consolidation, across
  // however many shipments (and sailings) its items are actually split
  // over — `shipments` already only holds invoiceable, un-invoiced items
  // (see getInvoiceableShipments), so this never pulls in anything already
  // billed. Lets an admin bill a whole consolidation with one click instead
  // of hunting down each of its items individually.
  const selectFullConsolidation = (consolidationId) => {
    let matchCount = 0;
    setSelectedItems((prev) => {
      const next = new Map(prev);
      for (const shipment of shipments) {
        for (const item of shipment.items || []) {
          if (item.consolidation?._id === consolidationId) {
            matchCount += 1;
            next.set(item._id, {
              shipmentId: shipment._id,
              itemId: item._id,
              customerId: shipment.customer?._id,
            });
          }
        }
      }
      return next;
    });
    return matchCount;
  };

  // Arrived via AdminConsolidations' "Invoice Consolidation" button — waits
  // for the (mode 0 / Browse All) shipment list to actually load, then
  // auto-selects this consolidation's items exactly like clicking "Select
  // Full Consolidation" by hand would. Only runs once per page visit (the
  // ref guard) — selectedItems is the admin's to edit freely afterward.
  useEffect(() => {
    if (!consolidationIdParam || autoSelectedConsolidationRef.current || loadingShipments) return;
    autoSelectedConsolidationRef.current = true;
    const count = selectFullConsolidation(consolidationIdParam);
    if (count === 0) {
      enqueueSnackbar("No un-invoiced items found for this consolidation", { variant: "warning" });
    } else {
      enqueueSnackbar(`Selected ${count} item${count === 1 ? "" : "s"} from this consolidation`, { variant: "success" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidationIdParam, loadingShipments, shipments]);
  const toggleShipment = (shipment, items) => {
    const allSelected = shipmentAllSelected(items);
    setSelectedItems((prev) => {
      const next = new Map(prev);
      items.forEach((item) => {
        if (allSelected) {
          next.delete(item._id);
        } else {
          next.set(item._id, {
            shipmentId: shipment._id,
            itemId: item._id,
            customerId: shipment.customer?._id,
          });
        }
      });
      return next;
    });
  };

  const setCalcField = (field) => (e) => setCalcInputs((p) => ({ ...p, [field]: e.target.value }));
  const setLineAmount = (index) => (e) => {
    const value = e.target.value;
    setCalcLines((prev) => prev.map((l, i) => (i === index ? { ...l, amount: value } : l)));
  };

  const addExtraLine = () => {
    extraLineIdRef.current += 1;
    setExtraLines((prev) => [...prev, { id: extraLineIdRef.current, description: "", amount: "" }]);
  };
  const removeExtraLine = (id) => setExtraLines((prev) => prev.filter((l) => l.id !== id));
  const setExtraLineField = (id, field) => (e) => {
    const value = e.target.value;
    setExtraLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const previousDuesTotal = previousDueLines.reduce((s, l) => s + l.unitPrice, 0);
  const promoTotal = promoLines.reduce((s, l) => s + l.unitPrice, 0);

  const calcLinesTotal = calcLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const extraLinesTotal = extraLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const dutiesTotal = parseFloat(dutiesAmount) || 0;
  // What the discount percentage (if that mode is chosen) is computed
  // against — the fresh charges actually being billed on this invoice.
  // Carried-forward previous dues and promo redemptions are both fixed,
  // already-settled-elsewhere amounts, not part of what a one-off discount
  // here should reduce.
  const freshChargesSubtotal = calcLinesTotal + extraLinesTotal + dutiesTotal;
  const discountRaw =
    discountType === "percent"
      ? (freshChargesSubtotal * (parseFloat(discountValue) || 0)) / 100
      : parseFloat(discountValue) || 0;
  // Never let the discount alone push the fresh charges negative — clamped
  // to the subtotal it's computed against, not the invoice's grand total
  // (previous dues/promo lines are untouchable, per the comment above).
  const discountTotal = Math.min(Math.max(discountRaw, 0), Math.max(freshChargesSubtotal, 0));

  const total = freshChargesSubtotal - discountTotal + previousDuesTotal + promoTotal;

  const selectedCustomerInfo = useMemo(() => {
    if (!activeCustomerId) return null;
    for (const s of shipments) {
      if (s.customer?._id === activeCustomerId) return s.customer;
    }
    return null;
  }, [activeCustomerId, shipments]);

  const buildInvoicePayload = () => {
    const lines = [
      ...calcLines
        .filter((l) => parseFloat(l.amount))
        .map((l) => ({ description: l.description, unitPrice: parseFloat(l.amount), quantity: 1 })),
      ...extraLines
        .filter((l) => l.description.trim() && parseFloat(l.amount))
        .map((l) => ({ description: l.description.trim(), unitPrice: parseFloat(l.amount), quantity: 1 })),
      ...(dutiesTotal > 0 ? [{ description: "Duties", unitPrice: dutiesTotal, quantity: 1 }] : []),
      ...(discountTotal > 0
        ? [
            {
              description:
                discountType === "percent"
                  ? `Discount (${parseFloat(discountValue) || 0}%)`
                  : "Discount",
              unitPrice: -discountTotal,
              quantity: 1,
            },
          ]
        : []),
      ...previousDueLines,
    ];

    return {
      customer: activeCustomerId,
      items: itemRefs,
      lines,
      dueDate,
      notes,
      // No promo ids to send — the discount is read straight off the
      // redemption ledger server-side (invoiceController.createInvoice
      // calls findUnbilledRedemptions), since the redemption itself
      // already happened back when the shipment was created. Nothing here
      // can add, remove, or tamper with it.
    };
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await invoiceService.create(buildInvoicePayload());
      enqueueSnackbar("Invoice generated successfully", { variant: "success" });
      navigate("/admin/invoices");
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to generate invoice", { variant: "error" });
    } finally {
      setGenerating(false);
    }
  };

  const reviewPanel = (
    <Card sx={{ position: "sticky", top: 16, boxShadow: selectedItems.size > 0 ? 6 : 0 }}>
      {selectedItems.size === 0 ? (
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Select items on the left to review and generate an invoice.
          </Typography>
        </CardContent>
      ) : (
        <>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Calculate fontSize="small" />
                <Typography fontWeight={700} fontSize={14}>
                  Review & Generate ({selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""})
                </Typography>
              </Box>
            }
          />
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {calcError && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {calcError}
              </Alert>
            )}
            {selectedIncompleteConsolidationItems.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {selectedIncompleteConsolidationItems.length} selected item
                {selectedIncompleteConsolidationItems.length !== 1 ? "s are" : " is"} still in a consolidation that
                hasn't completed yet — billing{" "}
                {selectedIncompleteConsolidationItems.length !== 1 ? "them" : "it"} now is fine if the customer wants
                this item invoiced separately, but double-check that's intended.
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                  {selectedIncompleteConsolidationItems.map(({ item, shipment }) => (
                    <Typography key={item._id} component="li" variant="caption" display="block">
                      {item.description} ({shipment.trackingNumber}) — {item.consolidation.consolidationNumber}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
              <TextField
                size="small"
                label="Total CBF"
                type="number"
                value={calcInputs.cbf}
                onChange={setCalcField("cbf")}
                helperText="Summed from selected items"
              />
              <TextField
                size="small"
                label="Weight (lbs)"
                type="number"
                value={calcInputs.weightLb}
                onChange={setCalcField("weightLb")}
                helperText="Summed from selected items"
              />
              <TextField
                size="small"
                label="Merchandise Value"
                type="number"
                value={calcInputs.merchandiseValue}
                onChange={setCalcField("merchandiseValue")}
                helperText="Summed from selected items"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!calcInputs.deliverySelected}
                    onChange={(e) => setCalcInputs((p) => ({ ...p, deliverySelected: e.target.checked }))}
                  />
                }
                label="Delivery"
              />
              <TextField
                size="small"
                label="Due Date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                required
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={calculating ? <CircularProgress size={14} /> : <Calculate />}
                onClick={() => runCalculate()}
                disabled={calculating}
              >
                Recalculate
              </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {calcLines.map((line, i) => (
                <Box key={line.key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {line.description}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={line.amount}
                    onChange={setLineAmount(i)}
                    sx={{ width: 120 }}
                  />
                </Box>
              ))}
              {/* Billed Duties line — grouped alongside Shipping/Insurance/Port &
                  Handling since it's the same kind of per-invoice carrier/customs
                  charge, not an ad-hoc extra charge or a discount. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Duties
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="0.00"
                  value={dutiesAmount}
                  onChange={(e) => setDutiesAmount(e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Extra Charges
              </Typography>
              {extraLines.map((line) => (
                <Box key={line.id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder="Description (e.g. Storage Fee)"
                    value={line.description}
                    onChange={setExtraLineField(line.id, "description")}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Amount"
                    value={line.amount}
                    onChange={setExtraLineField(line.id, "amount")}
                    sx={{ width: 120 }}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeExtraLine(line.id)}
                    sx={{ minWidth: 0, p: 0.75 }}
                  >
                    <Delete fontSize="small" />
                  </Button>
                </Box>
              ))}
              <Button size="small" startIcon={<Add />} onClick={addExtraLine} sx={{ alignSelf: "flex-start" }}>
                Add Charge
              </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Discount
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  size="small"
                  select
                  label="Discount Type"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="amount">Fixed Amount</MenuItem>
                  <MenuItem value="percent">Percentage</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label={discountType === "percent" ? "Discount (%)" : "Discount (CI$)"}
                  placeholder={discountType === "percent" ? "0" : "0.00"}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  slotProps={discountType === "percent" ? { htmlInput: { min: 0, max: 100 } } : undefined}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            {loadingDues && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
            {previousDues.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Previous Pending Dues
                </Typography>
                {previousDues.map((d) => (
                  <Box key={d._id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Checkbox
                      size="small"
                      checked={includedDueIds.has(d._id)}
                      onChange={() => toggleDueIncluded(d._id)}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Previous pending from Invoice {d.invoiceNumber}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${d.balanceDue.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {loadingPromoDiscounts && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
            {promoDiscounts.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Promo Discounts
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Already redeemed by the customer when they created these shipments — applied automatically.
                </Typography>
                {promoDiscounts.map((d) => (
                  <Box key={`${d.promoId}-${d.shipmentId}`} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{d.promoName}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Shipment {d.shipmentTrackingNumber}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      − ${d.discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {freightBySailing.length > 0 && (
              <Box
                sx={{
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                  Allocated Freight (from Manifest Cover Sheet)
                </Typography>
                {freightBySailing.map((s) => (
                  <Box
                    key={s.sailingId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.5,
                      "&:not(:last-of-type)": { borderBottom: "1px solid", borderColor: "divider" },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        Sailing {s.sailingNumber}
                      </Typography>
                      {s.hblNumber && (
                        <Typography variant="caption" color="text.secondary">
                          Manifest / HBOL {s.hblNumber}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      CI${s.customerAllocatedFreight.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {(dutiesTotal > 0 || discountTotal > 0) && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {dutiesTotal > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Duties</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${dutiesTotal.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {discountTotal > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">
                      Discount{discountType === "percent" ? ` (${parseFloat(discountValue) || 0}%)` : ""}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      − ${discountTotal.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <TextField
              size="small"
              label="Notes (optional)"
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography fontWeight={700}>Total Invoice Amount</Typography>
                <Typography fontWeight={700} fontSize={20} color="primary.main">
                  CI${total.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ≈ ${ciToUsd(total, usdToCiRate).toFixed(2)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => {
                  setPreviewPayload(buildInvoicePayload());
                  setPreviewOpen(true);
                }}
                disabled={generating || !activeCustomerId || !dueDate || total <= 0}
              >
                View Invoice
              </Button>
            </Box>
          </CardContent>
        </>
      )}
    </Card>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Generate Invoice"
        subtitle="Select items scheduled for sailing or later — by browsing all shipments or searching a customer — to bill on one invoice"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 420px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* Left: browse / search + item selection */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <Card>
            <Tabs
              value={mode}
              onChange={(_, v) => setMode(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ maxWidth: "100%", px: 2, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Tab label="Browse Invoiceable Shipments" sx={{ textTransform: "none", fontWeight: 600 }} />
              <Tab label="Search Customer" sx={{ textTransform: "none", fontWeight: 600 }} />
            </Tabs>
            <CardContent>
              {mode === 1 && (
                <Autocomplete
                  options={customerOptions}
                  getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
                  loading={loadingCustomerOptions}
                  value={selectedCustomer}
                  onChange={handleSelectCustomer}
                  inputValue={customerQuery}
                  onInputChange={(_, v) => setCustomerQuery(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Customer"
                      placeholder="Name or email"
                      slotProps={{
                        ...params.slotProps,
                        input: {
                          ...params.slotProps?.input,
                          endAdornment: (
                            <>
                              {loadingCustomerOptions && <CircularProgress color="inherit" size={18} />}
                              {params.slotProps?.input?.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}

              {activeCustomerId && (
                <Alert severity="info" sx={{ borderRadius: 2, mt: mode === 1 ? 2 : 0 }}>
                  Invoicing <strong>{selectedCustomerInfo?.firstName} {selectedCustomerInfo?.lastName}</strong> —
                  shipments from other customers are hidden while items are selected.
                </Alert>
              )}
            </CardContent>
          </Card>

          {loadingShipments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : shipments.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No invoiceable shipments"
              description={mode === 1 ? "This customer has no un-invoiced items scheduled for sailing or later." : "No shipments currently have un-invoiced items scheduled for sailing or later."}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {sailingGroups.map((group) => (
                <Card key={group.sailingId}>
                  <CardHeader
                    title={
                      group.sailingId === "no-sailing" ? (
                        <Typography fontWeight={700} fontSize={14} color="text.secondary">
                          No Sailing Assigned
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Typography fontWeight={700} fontSize={14}>
                            {group.vesselName}
                            {group.voyageNumber ? ` / ${group.voyageNumber}` : ""}
                          </Typography>
                          <StatusChip status={group.status} domain="sailing" />
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                            #{group.sailingNumber}
                          </Typography>
                        </Box>
                      )
                    }
                    subheader={
                      group.sailingId !== "no-sailing" && (
                        <Typography variant="caption" color="text.secondary">
                          Departs {dayjs(group.departureDate).format("MMM D, YYYY")} · ETA{" "}
                          {dayjs(group.estimatedArrivalDate).format("MMM D, YYYY")} · MBL#{" "}
                          {group.masterBillNumber || "—"}
                        </Typography>
                      )
                    }
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {group.shipments.map(({ shipment, items }) => (
                      <Box
                        key={shipment._id}
                        sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}
                      >
                        <Box
                          sx={{
                            bgcolor: "#001A3C",
                            px: 1.5,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={shipmentAllSelected(items)}
                            indeterminate={!shipmentAllSelected(items) && shipmentSomeSelected(items)}
                            onChange={() => toggleShipment(shipment, items)}
                            sx={{
                              p: 0.5,
                              color: "rgba(255,255,255,0.6)",
                              "&.Mui-checked": { color: "white" },
                              "&.MuiCheckbox-indeterminate": { color: "white" },
                            }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{ fontFamily: "monospace", color: "white" }}
                          >
                            {shipment.trackingNumber}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${shipment.customer?.firstName} ${shipment.customer?.lastName}`}
                            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700 }}
                          />
                          {shipment.vendor?.name && (
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                              {shipment.vendor.name}
                            </Typography>
                          )}
                          {items[0]?.hblNumber && (
                            <Chip
                              size="small"
                              label={items[0].hblNumber}
                              sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 700 }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
                          {items.map((item) => {
                            const inIncompleteConsolidation =
                              item.consolidation && item.consolidation.status !== consolidationCompletedKey;
                            return (
                              <Box key={item._id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                                <Checkbox
                                  size="small"
                                  checked={selectedItems.has(item._id)}
                                  onChange={() => toggleItem(shipment, item)}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                    <Typography variant="body2" noWrap>
                                      {item.description}
                                    </Typography>
                                    {item.consolidation && (
                                      <>
                                        {inIncompleteConsolidation ? (
                                          <Chip
                                            size="small"
                                            icon={<WarningAmber sx={{ fontSize: 13 }} />}
                                            label={`In consolidation ${item.consolidation.consolidationNumber} — not completed yet`}
                                            color="warning"
                                            variant="outlined"
                                            sx={{ fontSize: 10.5, height: 20 }}
                                          />
                                        ) : (
                                          <Chip
                                            size="small"
                                            label={`Consolidation ${item.consolidation.consolidationNumber}`}
                                            variant="outlined"
                                            sx={{ fontSize: 10.5, height: 20 }}
                                          />
                                        )}
                                        <Button
                                          size="small"
                                          startIcon={<CallMerge sx={{ fontSize: 13 }} />}
                                          onClick={() => selectFullConsolidation(item.consolidation._id)}
                                          sx={{ fontSize: 10.5, py: 0, minWidth: 0 }}
                                        >
                                          Select Full Consolidation
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    D/R {item.dockReceiptNumber || "—"} ·{" "}
                                    {item.weight != null ? `${item.weight} lb` : "no weight"} ·{" "}
                                    {item.cbf != null ? `${item.cbf} cbf` : "no cbf"} · $
                                    {(item.totalValue || 0).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Right: review & generate — sticky so it stays in view while the
            (often much longer) left-hand shipment/item list scrolls. */}
        <Box sx={{ position: "sticky", top: 16, maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}>
          {reviewPanel}
        </Box>
      </Box>

      <InvoicePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onEdit={() => setPreviewOpen(false)}
        payload={previewPayload}
        onConfirm={handleGenerate}
        confirming={generating}
      />
    </Box>
  );
};

export default AdminGenerateInvoice;
